import { useEffect, useState } from "react";

const SPEED = 150; // Characters per second

export function TypewriterWorkflowName({ name }: { name: string }) {
  const [text, setText] = useState(name);
  const [prevName, setPrevName] = useState(name);
  const [animating, setAnimating] = useState(false);
  // The queued backspace-then-type animation, captured at the transition so the
  // effect keys on it without reading anything during render. `from` is the
  // text on screen when the name changed; `target` is the new name.
  const [pending, setPending] = useState<{
    from: string;
    target: string;
  } | null>(null);

  if (name !== prevName) {
    const wasUntitled = prevName === "Untitled";
    setPrevName(name);
    // Only animate Untitled → a real name, and not while one is already running
    // (a mid-animation rename just swaps to the new name).
    if (wasUntitled && name !== "Untitled" && !animating) {
      setPending({ from: text, target: name });
    } else {
      // Clear any in-flight animation so its rAF loop (cancelled by the effect
      // cleanup) stops clobbering this name with slices of the old target.
      setPending(null);
      setText(name);
    }
  }

  useEffect(() => {
    if (!pending) return;
    let frame: number | null = null;
    let displayed = pending.from;
    let phase = 1; // 1 = backspacing, 2 = typing
    let lastTime = 0;
    // oxlint-disable-next-line react/react-compiler -- marks the rAF animation running for the render-phase guard
    setAnimating(true);

    const animate = (time: number) => {
      if (!lastTime) lastTime = time;
      const chars = Math.floor((time - lastTime) / (1000 / SPEED));
      if (chars > 0) {
        lastTime = time;
        if (phase === 1) {
          displayed = displayed.slice(0, Math.max(0, displayed.length - chars));
          setText(displayed);
          if (!displayed.length) phase = 2;
        } else {
          const len = Math.min(displayed.length + chars, pending.target.length);
          displayed = pending.target.slice(0, len);
          setText(displayed);
          if (len >= pending.target.length) {
            setAnimating(false);
            return;
          }
        }
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      setAnimating(false);
    };
  }, [pending]);

  // oxlint-disable-next-line react/jsx-no-useless-fragment -- component must return JSX
  return <>{text}</>;
}
